import React from 'react';
import classNames from 'classnames';
import { css } from '@emotion/react';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { ButtonProps } from './Button';
import { ButtonIcon } from './ButtonIcon';
import { TextButtonKind, TextButtonSize } from './types';
import {
  button_base_css,
  text_button_size_css,
  text_button_text_css,
  getTextButtonStyle,
  text_button_icon_size_css,
} from './styles';
import { semantic_colors } from '../../foundation';
import { IconLoadingDonuts } from '../icons/generated';

export interface TextButtonProps extends Omit<ButtonProps, 'loading' | 'kind' | 'size'> {
  /** @default 'text' */
  kind?: TextButtonKind;
  /** @default false */
  loading?: boolean;
  /** @default false */
  underline?: boolean;
  /** @default 'medium' */
  size?: TextButtonSize;
  /**
   * @default
   * kind='text'
   * semantic_colors.content.primary
   * kind='link'
   * semantic_colors.accent.primary
   */
  color?: string;
}

export const TextButton = React.forwardRef<HTMLButtonElement, TextButtonProps>(
  (
    {
      type = 'button',
      kind = 'text' as TextButtonKind,
      underline = false,
      size = 'medium',
      color,
      fill,
      loading,
      disabled,
      startIcon,
      endIcon,
      className,
      children,
      asChild = false,
      ...props
    },
    ref,
  ) => {
    const isTextKind = kind === 'text';
    const isDisabled = loading || disabled;
    const text_kind_color = color ?? semantic_colors.content.primary;
    const link_kind_color = color ?? semantic_colors.accent.primary;
    const fixed_color = isTextKind ? text_kind_color : link_kind_color;
    const icon_props = {
      size: text_button_icon_size_css[size],
      ...(!isTextKind && {
        color: 'currentColor',
      }),
    };
    const loading_indicator = loading && (
      <ButtonIcon {...icon_props}>
        <IconLoadingDonuts size={30} />
      </ButtonIcon>
    );
    const start_icon_node = startIcon && (
      <ButtonIcon {...icon_props}>{loading ? <IconLoadingDonuts size={30} /> : startIcon}</ButtonIcon>
    );
    const end_icon_node = endIcon && (
      <ButtonIcon {...icon_props}>{loading && !startIcon ? <IconLoadingDonuts size={30} /> : endIcon}</ButtonIcon>
    );
    const Comp = asChild ? Slot : 'button';

    return (
      <Comp
        type={type}
        className={classNames('pds-textbutton', className)}
        css={[
          button_base_css,
          text_button_text_css[size],
          isTextKind && text_button_size_css[size],
          getTextButtonStyle(kind, fixed_color),
          css`
            ${fill && 'width: 100%;'};
            ${underline && 'text-decoration: underline'};
            cursor: ${isDisabled ? 'not-allowed' : 'pointer'};
            border: none;
          `,
        ]}
        disabled={isDisabled}
        {...props}
        ref={ref}
      >
        {start_icon_node || (!end_icon_node && loading_indicator)}
        <Slottable>{children}</Slottable>
        {end_icon_node}
      </Comp>
    );
  },
);
